/**
 * System durability and availability — MTTDL/MTTNA model.
 *
 * Both outputs come from one general mean-time-to-data-loss form, supplied by
 * the architect (CONF-94 design review, 2026-09-17):
 *
 *                (n - C - 1)! * MTBF^(C+1)
 *     MTTDL  =   -------------------------        n = erasure-coding width (data + parity)
 *                       n! * MTTR^C               C = parity chunks
 *
 * Distributed-RAID scaling, where D is the number of devices in the pool:
 *
 *     MTTDL_dR = MTTDL * (D / n) ^ (C * (C - 1) / 2)
 *
 * Then, over t = 8760 hours (one year):
 *
 *     durability = e^(-t / MTTDL_dR)
 *     nines      = -log10(1 - durability)
 *
 * with MTTR = (data to rebuild) / (rebuild rate) and MTBF = 8760 / AFR.
 *
 * The failure unit is the OSD (one VPOD: one NVMe namespace plus its share of the
 * JBOD's HDDs). Today ANY drive in an OSD kills it, because the HDDs are a plain
 * ZFS stripe. CONF-94 adds the MLEC case, where the HDDs form a raidz1 vdev.
 *
 * See docs/superpowers/specs/2026-09-17-conf-94-mlec-design.md sections 2.2 and 2.5.
 */
import { getVendorMetricsDb } from '../../config/vendorRegistry';

/** Hours in a year. The model's observation window and the AFR -> MTBF divisor. */
const HOURS_PER_YEAR = 8760;

/**
 * Convert a probability of survival into "nines" (0.999 -> 3).
 * This is the module's entire output format, so it gets a name.
 */
const ninesFrom = (survivalProbability) => -Math.log10(1 - survivalProbability);

/**
 * The general MTTDL form above, in hours.
 *
 * Implemented as the falling-factorial product n * (n-1) * ... * (n-C) rather than
 * as factorial(n - C - 1) / factorial(n). The two are algebraically identical, but
 * the product reproduces the exact floating-point operation order of the
 * hand-expanded expressions this replaced, so the pinned characterization snapshot
 * stays bit-identical. Do not "simplify" it into factorials.
 *
 * Exported for direct unit testing of the algebraic identity (see mttdlRaid.test.js)
 * and for reuse by the MLEC local level.
 *
 * @param {number} width      n — erasure-coding width, > C. Normally an integer;
 *                            the MLEC local level passes 7.5 on the SMC 4U90
 *                            (90 HDDs / 12 VPODs), where the smooth falling
 *                            factorial lands within 0.5% of the physical 7/8
 *                            mix — see mlecDurability.test.js.
 * @param {number} parity     C — parity chunks.
 * @param {number} mtbfHours  MTBF of one member device, in hours.
 * @param {number} mttrHours  MTTR of one member device, in hours.
 * @returns {number} MTTDL in hours.
 */
export const mttdlRaid = ({ width, parity, mtbfHours, mttrHours }) => {
  let widthProduct = 1;
  for (let i = 0; i <= parity; i += 1) {
    widthProduct *= width - i;
  }
  return Math.pow(mtbfHours, parity + 1) / (widthProduct * Math.pow(mttrHours, parity));
};

/**
 * Distributed-RAID scaling factor: (D / n) ^ (C(C-1)/2).
 * Note it is inert at C = 1 (exponent 0), which is why a single-parity local level
 * needs no special-casing.
 *
 * Exported for direct unit testing of the algebraic identity (see mttdlRaid.test.js)
 * and for reuse by the MLEC local level.
 */
export const distributedRaidFactor = ({ devices, width, parity }) =>
  Math.pow(devices / width, (parity * (parity - 1)) / 2);

// Utility functions for calculating system durability and availability
export const calculateSystemReliability = (config) => {
  // Use the hardwareVendor from the config, defaulting to 'AIC' if not provided
  const hardwareVendor = config.hardwareVendor || 'AIC';
  const metricsDb = getVendorMetricsDb(hardwareVendor);

  // All LAMBDA_* are annual failure rates (fraction of the population per year).
  const lambdaSsdPerYear = metricsDb.global.LAMBDA_SSD;
  const lambdaHddPerYear = metricsDb.global.LAMBDA_HDD;
  const lambdaServerPerYear = metricsDb.global.LAMBDA_Server;
  const observationYears = metricsDb.global.TIME_YEAR;
  const availabilityMttrHours = metricsDb.global.AvailabilityMTTR;
  const dualActuatorXferRate = metricsDb.global.dualActuatorXferRate;   // MB/s
  const singleActuatorXferRate = metricsDb.global.singleActuatorXferRate; // MB/s
  const ssdPerVpod = metricsDb.global.ssdPerVpod;

  const { jbodSize, nodeCount, vpodHddCapacity, vpodSsdCapacity, mlec } = config;
  const [dataBits, parityBits] = config.encodingScheme.split('+').map(Number);

  const codingWidth = dataBits + parityBits;

  // All chassis use 12 OSDs per JBOD, so HDDs/OSD = drives / 12
  // (reproduces 30->2.5, 78->6.5, 108->9 exactly; adds 60->5, 90->7.5).
  //
  // KNOWN DIVERGENCE, deliberately preserved: osdSizeTb below divides by
  // ssdPerVpod instead (16 on DELL), so on DELL the same physical quantity has
  // two values here — 5.0 and 3.75 on a 60-bay. Reconciling it changes DELL and
  // SMC output, so it is tracked with the other input corrections in the design
  // doc section 6, not fixed here.
  const drivesPerJbod = jbodSize || 78;
  const numHddPerOsd = drivesPerJbod / 12;

  /**
   * CONF-94. MLEC makes each VPOD's HDDs a single ZFS raidz1 vdev, so one HDD can
   * fail without killing the OSD — ZFS resilvers it locally, with no network
   * traffic. That is the general MTTDL form at n = HDDs per VPOD, C = 1.
   *
   * MTTR_local is one drive's data over one drive's rate: a raidz1 resilver is
   * bounded by writing the single replacement drive, not by reading the survivors.
   * The 0.25 factor is the same client-contention allowance used for the OSD-level
   * rebuild above.
   *
   * This value is deliberately not agonised over. Once the HDD contribution
   * collapses (0.090 -> 0.000137 per year on a 4U108), LAMBDA_SSD dominates at
   * ~97% of the OSD failure rate, so the whole plausible resilver-rate range is
   * worth 0.03 nines. mlecDurability.test.js pins that insensitivity.
   */
  const localResilverMttrHours = () => {
    const rebuildRateMbPerSec = vpodHddCapacity === 18
      ? dualActuatorXferRate * 0.25
      : singleActuatorXferRate * 0.25;
    return ((vpodHddCapacity * 1000000) / rebuildRateMbPerSec) / 3600;
  };

  const MLEC_LOCAL_PARITY = 1; // raidz1: exactly one parity drive per VPOD.

  /**
   * The HDD contribution to the OSD's annual failure rate.
   *
   * Without MLEC the HDDs are a plain stripe, so ANY of them failing kills the
   * OSD and the contribution is simply the sum of their rates. With MLEC the
   * contribution is the local level's own rate of data loss — two HDDs in the
   * same VPOD failing inside one resilver window.
   */
  const hddFailureRatePerYear = mlec
    ? HOURS_PER_YEAR / mttdlRaid({
        width: numHddPerOsd,
        parity: MLEC_LOCAL_PARITY,
        mtbfHours: HOURS_PER_YEAR / lambdaHddPerYear,
        mttrHours: localResilverMttrHours(),
      })
    : numHddPerOsd * lambdaHddPerYear;

  // An OSD dies if the NVMe fails, or if its HDDs lose data. Without MLEC the
  // latter means any single HDD failing; with MLEC it means the local raidz1
  // losing data. The NVMe is a single device either way — MLEC cannot protect it,
  // which is why AFA has no MLEC option at all.
  const durabilityAfr =
    1 - Math.exp(-1 * (hddFailureRatePerYear + 1 * lambdaSsdPerYear) * observationYears);

  // Availability's failure unit is the whole server, not the OSD.
  const availabilityAfr = lambdaServerPerYear;

  const durabilityMtbfHours = HOURS_PER_YEAR / durabilityAfr;
  const availabilityMtbfHours = HOURS_PER_YEAR / availabilityAfr;

  /**
   * Time to rebuild one dead OSD over the network, in hours.
   *
   * KNOWN DIVERGENCE, deliberately preserved: the SSD term here does not match
   * raw2Useable's getUsableVpodSsdCapacity lookup — this uses (capacity - 1) on a
   * 108-bay and half the capacity otherwise. Two models of one quantity; tracked
   * in the design doc section 6.
   *
   * The 0.25 factor is the share of each drive's sequential bandwidth assumed
   * available to a rebuild while the system continues serving clients.
   */
  const calculateDurabilityMttrHours = () => {
    const osdSizeTb = jbodSize === 108
      ? (((drivesPerJbod / ssdPerVpod) * vpodHddCapacity) + (vpodSsdCapacity - 1))
      : (((drivesPerJbod / ssdPerVpod) * vpodHddCapacity) + (0.5 * vpodSsdCapacity));
    // 18 TB is the dual-actuator SKU: two actuators per spindle, so twice the
    // sequential rate. Detected by capacity because that is the only signal the
    // config carries here.
    const rebuildRateMbPerSec = vpodHddCapacity === 18
      ? numHddPerOsd * dualActuatorXferRate * 0.25
      : numHddPerOsd * singleActuatorXferRate * 0.25;
    const mttrSeconds = (osdSizeTb * 1000000) / rebuildRateMbPerSec; // TB -> MB
    return mttrSeconds / 3600;
  };

  const durabilityMttrHours = calculateDurabilityMttrHours();

  // Network level: the PanFS D+P stripe across OSDs. C = parityBits (2 in practice).
  const mttdlNetworkHours = mttdlRaid({
    width: codingWidth,
    parity: parityBits,
    mtbfHours: durabilityMtbfHours,
    mttrHours: durabilityMttrHours,
  });

  const mttdlDistributedHours = mttdlNetworkHours * distributedRaidFactor({
    devices: nodeCount * ssdPerVpod, // total OSDs in the pool
    width: codingWidth,
    parity: parityBits,
  });

  /**
   * Mean time to NOT AVAILABLE, in hours.
   *
   * KNOWN DIVERGENCE from the general form above, deliberately preserved: both
   * branches raise MTTR to C+1, where the durability form uses C. Branch two is
   * MTBF^3/(w(w-1)(w-2) * MTTR^3), not MTTR^2. Whether that is intentional or a
   * transcription slip is unresolved; changing it moves every quote's availability
   * figure, so it stays as-is and is NOT routed through mttdlRaid. Tracked in the
   * design doc section 6.
   *
   * The nodeCount < codingWidth branch covers systems too small to spread a full
   * stripe across distinct servers, where the server count bounds the exposure
   * rather than the stripe width.
   */
  const mttnaHours = () => {
    if (nodeCount < codingWidth) {
      return Math.pow(availabilityMtbfHours, 2)
        / (nodeCount * (nodeCount - 1) * Math.pow(availabilityMttrHours, 2));
    }
    return Math.pow(availabilityMtbfHours, 3)
      / ((codingWidth * (codingWidth - 1) * (codingWidth - 2))
         * Math.pow(availabilityMttrHours, 3));
  };

  const mttnaDistributedHours = () => {
    if (nodeCount < codingWidth) {
      return mttnaHours();
    }
    return mttnaHours() * Math.pow(nodeCount / codingWidth, (parityBits * (parityBits - 1)) / 2);
  };

  const durability = Math.exp(-HOURS_PER_YEAR / mttdlDistributedHours);
  const durabilityNines = ninesFrom(durability);

  const availability = Math.exp(-HOURS_PER_YEAR / mttnaDistributedHours());
  // You cannot be more available than you are durable: data you have lost cannot be
  // served. Written as a conditional rather than Math.min because Math.min propagates
  // NaN where the original did not — on a malformed config that yields a NaN
  // durability, availability must still report its own value, exactly as before.
  const availabilityNinesUnclamped = ninesFrom(availability);
  const availabilityNines = availabilityNinesUnclamped > durabilityNines
    ? durabilityNines
    : availabilityNinesUnclamped;

  return {
    durability,
    durabilityNines,
    availability,
    availabilityNines,
  };
};

// Export individual functions for direct access
export const calculateDurabilityNines = (config) => {
  const { durabilityNines } = calculateSystemReliability(config);
  return durabilityNines;
};

export const calculateAvailabilityNines = (config) => {
  const { availabilityNines } = calculateSystemReliability(config);
  return availabilityNines;
};
